import CloseIcon from "@mui/icons-material/Close";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Search from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import { Box, Chip, Collapse, Divider, Drawer, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useJobColors } from "../../../../hooks/useJobColors";
import { CONTROL_HEIGHT, CONTROL_HEIGHT_PX, INK, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import type { Job } from "../../../../utils/models";
import { CHAT_ACTION_BAR_REVEAL_SX, ChatActionBar } from "../../../UIToolkit/Chat/ChatActionBar";
import { ChatActionButton } from "../../../UIToolkit/Chat/ChatActionButton";
import { CoverageDeltaCell } from "../../../UIToolkit/Coverage/CoverageDeltaCell";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";
import { activateOnKey } from "./a11y";
import { MonthlyDayActions } from "./MonthlyDayActions";
import {
	buildDaySidebarData,
	MONTHLY_OTHER_WINDOW,
	MONTHLY_UNASSIGNED_JOB_ID,
	type MonthlyDayBoundary,
	type MonthlyDayData,
	type MonthlyDayFilters,
	type MonthlyShiftWindow,
	type MonthlySidebarWindow,
	matchesStaffSearch,
	resolveWindowBand,
} from "./monthlyJobUtils";
import { getWindowPresentation } from "./monthlyWindowPresentation";
import { useMonthlyDayActions } from "./useMonthlyDayActions";

export const MONTHLY_SIDEBAR_WIDTH = 440;

/**
 * The window row's number box — the list's right-hand ledger column.
 *
 * Every row reserves it whether or not it is graded, so `3/40` and a bare `2` land on the same right
 * edge and the column reads straight down the drawer instead of ragging with each row's content.
 */
const SIDEBAR_METRIC_WIDTH = 58;
const SIDEBAR_METRIC_HEIGHT = 22;

type Props = {
	day: MonthlyDayData | undefined;
	/** Whether the day is in the schedule, so the staffing panels can act on it — see `MonthlyVisibleDay.isInSchedule`. */
	isInSchedule: boolean;
	date: DateTime;
	showActual: boolean;
	visibleJobs: Job[];
	/** The department's derived windows — the drawer paints each exact window in its cell row's colour. */
	windows: readonly MonthlyShiftWindow[];
	/** The department's day boundary, so a window outside the derived set still bands by daylight. */
	dayBoundary: MonthlyDayBoundary;
	departmentId: string;
	/** The grid's active window/role/job/location filters, so a drilled-into day shows the same totals. */
	filters: MonthlyDayFilters;
	/** Auto-scheduler departments don't post shifts — gates the header action and the per-window chip. */
	isAutoScheduler: boolean;
	initialFocusJobId?: string;
	/** Leaves the month for this day's full Day view — the same callback the grid tile's jump uses. */
	onOpenDayView: (date: DateTime) => void;
	onClose: () => void;
};

const WindowRow: FC<{
	window: MonthlySidebarWindow;
	presentation: ReturnType<typeof getWindowPresentation>;
	selectedJobIds: ReadonlySet<string>;
	searchText: string;
	departmentId: string;
	/**
	 * Raises the day's post-shift action prefilled with THIS window. Owned by the drawer, not by this row,
	 * so every action on this screen goes through the one shared layer and one panel instance.
	 */
	onPostWindow: (window: MonthlySidebarWindow) => void;
	/** `false` while the day's actions are blocked (draft mode) — the chip must not offer what it can't do. */
	canPost: boolean;
}> = ({ window, presentation, selectedJobIds, searchText, departmentId, onPostWindow, canPost }) => {
	const { t } = useTranslation();
	const jobColor = useJobColors();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	// Every row mounts collapsed. The drawer opens as a list of the day's shift times with their
	// headcounts — one screen you can read top to bottom — and expanding is how you ask for names.
	// Searching still opens whatever matched, which is the one case where the user has already said
	// which row they want.
	const [expanded, setExpanded] = useState(false);

	const normalizedSearch = searchText.trim().toLowerCase();
	const jobsToShow = useMemo(
		() =>
			window.jobs
				.filter((group) => selectedJobIds.size === 0 || selectedJobIds.has(group.jobId))
				.map((group) => ({ ...group, employees: group.employees.filter((employee) => matchesStaffSearch(employee, normalizedSearch)) }))
				.filter((group) => group.employees.length > 0),
		[window.jobs, selectedJobIds, normalizedSearch],
	);

	const visibleScheduled = jobsToShow.reduce((sum, group) => sum + group.employees.length, 0);
	const isJobFiltered = selectedJobIds.size > 0;
	// Nothing left to show once a search or a job chip has emptied this row. Every window that reaches
	// here has somebody on it — the drawer no longer lists configured slots nobody is scheduled against —
	// so an empty row can only mean the filter excluded everyone, and keeping it would be a row of zero.
	if ((normalizedSearch || isJobFiltered) && visibleScheduled === 0) return null;

	// A search that has SURVIVED to this row matched somebody inside it, so the row opens regardless of
	// its own toggle: making the user expand the row their query just found would be the search failing.
	const isSearchPinnedOpen = normalizedSearch !== "";
	const isOpen = expanded || isSearchPinnedOpen;

	return (
		<Box sx={{ borderTop: `1px solid ${NEUTRAL[200]}` }}>
			{/* ONE row grammar, left to right: disclosure · band · the shift time · its numbers · its action.
			    Everything that used to compete for the right-hand half is gone or demoted — `N scheduled` said
			    in words what the fraction beside it already said in figures, and the action was a full text
			    chip repeated on every row, so the loudest thing in a list of shift times was the same four
			    words over and over. The numbers now own the right edge as a single fixed-width ledger column
			    (so they line up down the list whether or not a row is graded), and the action is a micro icon
			    in the app's standard row-action bar, at rest invisible until the row is hovered or focused. */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					py: 0.75,
					px: 1,
					position: "relative",
					backgroundColor: presentation.tint,
					...CHAT_ACTION_BAR_REVEAL_SX,
				}}
			>
				{/* The band rail. The grid tile dropped its own (its rows are pill-shaped now); here the rows are
				    full-width list rows, so the rail still earns its keep as the column-grouping scan signal. */}
				<Box aria-hidden sx={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "3px", backgroundColor: presentation.rail }} />
				{/* Disabled, not merely ineffective, while a search pins the row open. The toggle used to stay
				    live and do nothing: the click flipped `expanded`, `isOpen` stayed true because the search
				    still forced it, and `aria-expanded` went on reporting a state the control could not
				    change. A control that announces it can do something it cannot is worse than one that says
				    it is unavailable, so the search owns the disclosure for as long as it is filtering. */}
				<SystemButton
					buttonVariant="borderless"
					iconButton
					disabled={isSearchPinnedOpen}
					aria-expanded={isOpen}
					aria-label={t(isOpen ? "monthly.view.collapse.window" : "monthly.view.expand.window", { window: window.label })}
					onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
					data-testid={`monthly-sidebar-window-toggle-${window.key}`}
					sx={{ width: CONTROL_HEIGHT_PX.MICRO, height: CONTROL_HEIGHT_PX.MICRO, minWidth: CONTROL_HEIGHT_PX.MICRO, flexShrink: 0 }}
				>
					{isOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
				</SystemButton>
				{presentation.glyph}
				<Typography sx={{ fontSize: "13px", fontWeight: 700, color: presentation.ink, flex: 1, minWidth: 0 }}>{window.label}</Typography>
				{/* The row's one number, in a fixed box every row reserves so the column is straight.
				    Always a plain headcount — the drawer carries no requirement at all now, so there is no
				    `have/need` fraction and nothing to grade. It answers "who is working this window", and
				    the number is how many. */}
				<Box sx={{ width: SIDEBAR_METRIC_WIDTH, height: SIDEBAR_METRIC_HEIGHT, borderRadius: 0.75, overflow: "hidden", flexShrink: 0 }}>
					<CoverageDeltaCell
						filled={isJobFiltered ? visibleScheduled : window.scheduled}
						target={0}
						minimum={0}
						required={false}
						displayMode="filled"
						emphasis="count"
						// Yields the cell's 28px floor to this row's box, which is shorter and clips: an un-lowered
						// floor would push the number itself out of the visible area.
						sx={{ minHeight: SIDEBAR_METRIC_HEIGHT }}
						ariaLabel={`${window.label} ${t("monthly.view.scheduled.count", { count: isJobFiltered ? visibleScheduled : window.scheduled })}`}
						dataTestId={`monthly-sidebar-coverage-${window.key}`}
					/>
				</Box>
				{/* Fixed-width action slot, reserved whether or not it holds anything, so the ledger column
				    above never shifts between rows or on hover. Posting needs a real end time — a requirement
				    with no configured `shift_end` has none to offer. Deliberately NOT the header's generic
				    "post a shift": this opens the SAME Post Shift panel with this window's times (and its job,
				    when it holds exactly one) already filled in, which is why it wears the header's own
				    post glyph and says so in its tooltip and accessible name. */}
				<Box sx={{ width: CONTROL_HEIGHT.MICRO, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
					{window.endTime !== null && canPost && (
						<ChatActionBar showOnCoarsePointer sx={{ mt: 0 }}>
							<ChatActionButton
								label={t("monthly.view.post.this.window.named", { window: window.label })}
								icon={<SendIcon />}
								onClick={() => onPostWindow(window)}
								dataTestId={`monthly-sidebar-post-window-${window.key}`}
							/>
						</ChatActionBar>
					)}
				</Box>
			</Box>
			<Collapse in={isOpen} unmountOnExit>
				<Box sx={{ pb: 0.5 }}>
					{jobsToShow.length === 0 && (
						<Typography sx={{ px: 2, py: 1, fontSize: "12px", color: INK.SECONDARY }}>{t("monthly.view.no.shifts.this.window")}</Typography>
					)}
					{jobsToShow.map((group) => (
						<Box key={group.jobId} sx={{ px: 1, py: 0.5 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
								<Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: jobColor(group.jobId).dot }} />
								<Typography sx={{ fontSize: "11px", fontWeight: 700, color: INK.SECONDARY, textTransform: "uppercase" }}>{group.jobName}</Typography>
								<Typography sx={{ fontSize: "11px", color: INK.DISABLED }}>{t("monthly.view.scheduled.count", { count: group.employees.length })}</Typography>
							</Box>
							{group.employees.map((employee) => {
								const openProfile = () => {
									track("monthly_view_employee_profile_clicked", { nurse_id: employee.nurseId });
									navigate(`/${departmentId}/nurse/${employee.nurseId}`);
								};
								return (
									<Box
										key={employee.shiftId}
										data-testid={`monthly-sidebar-employee-${employee.shiftId}`}
										role="button"
										tabIndex={0}
										aria-label={employee.role === null ? employee.name : `${employee.name}, ${employee.role}`}
										onClick={openProfile}
										onKeyDown={activateOnKey(openProfile)}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1,
											py: 0.5,
											pl: 1.5,
											borderRadius: 1,
											cursor: "pointer",
											"&:hover": { backgroundColor: NEUTRAL[100] },
										}}
									>
										<Box
											sx={{
												width: 24,
												height: 24,
												borderRadius: "50%",
												backgroundColor: jobColor(group.jobId).fill,
												color: jobColor(group.jobId).ink,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: "11px",
												fontWeight: 700,
												flexShrink: 0,
											}}
										>
											{employee.name.trim().charAt(0).toUpperCase()}
										</Box>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography
												sx={{ fontSize: "13px", fontWeight: 500, color: INK.PRIMARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
											>
												{employee.name}
											</Typography>
											<Typography sx={{ fontSize: "11px", color: INK.SECONDARY }}>{employee.role}</Typography>
										</Box>
										{employee.location && <Typography sx={{ fontSize: "11px", color: INK.SECONDARY, flexShrink: 0 }}>{employee.location}</Typography>}
									</Box>
								);
							})}
						</Box>
					))}
				</Box>
			</Collapse>
		</Box>
	);
};

/**
 * Per-day drill-down: exact shift-time windows → jobs → employees.
 *
 * This is where the chip row's hour rounding is undone — a `2p–10:30p` window keeps its real, minute
 * accurate label here, nested under the same band colour its `2p–10p` cell row carries, so drilling in
 * CONFIRMS what the cell said instead of re-teaching it.
 */
export const MonthlyDaySidebar: FC<Props> = ({
	day,
	isInSchedule,
	date,
	showActual,
	visibleJobs,
	windows,
	dayBoundary,
	departmentId,
	filters,
	isAutoScheduler,
	initialFocusJobId,
	onOpenDayView,
	onClose,
}) => {
	const { t } = useTranslation();
	const [selectedJobIds, setSelectedJobIds] = useState<ReadonlySet<string>>(() => (initialFocusJobId ? new Set([initialFocusJobId]) : new Set()));
	const [searchText, setSearchText] = useState("");

	const data = useMemo(() => buildDaySidebarData({ day, showActual, visibleJobs, windows, filters }), [day, showActual, visibleJobs, windows, filters]);

	/**
	 * Painted from the DERIVED window set first, matched on the rounded key and then on the start hour, so
	 * a `6a–2p` cell row and its `6:00–14:30` drawer rows can never disagree about which band they are.
	 *
	 * A row OUTSIDE that set still gets its band. The derived set is capped — it is the handful of windows
	 * the department actually runs often enough to earn a chip — so the drawer, which lists every exact
	 * window and every configured gap, routinely holds rows that are in no derived window at all. Falling
	 * those through to the band-less `Other` treatment was a real miss: an `11p–7a` row rendered with no
	 * moon and a neutral grey rail, sitting directly beneath a `6p–6a` row that had both, so the list said
	 * one of the two nights was not a night. `resolveWindowBand` is the SAME day-start-relative derivation
	 * `deriveShiftWindows` bands its own windows with, so this classifies rather than guesses, and the
	 * `bandIndex` of 0 simply puts an unranked row on the band's lightest tint — the quietest step, which
	 * is the right weight for a row the department does not run often.
	 *
	 * `Other` is left for the one row that genuinely has no time to band: a requirement whose location
	 * configures no `shift_end` (see `MonthlySidebarWindow.endTime`).
	 */
	const presentationFor = useCallback(
		(window: MonthlySidebarWindow) => {
			// Matched on the ROUNDED key only. There used to be a second attempt matching on start hour
			// alone, which is not an identity: a department running `6a–2p` and `6a–6p` would hand the
			// second row the first one's `bandIndex`, so two different shift times drew in the same tint and
			// became indistinguishable in the list. A window outside the derived set gets its own band
			// below, which is a true answer rather than a neighbour's.
			const derived = windows.find((candidate) => candidate.key === window.roundedKey);
			if (derived) return getWindowPresentation(derived);
			return getWindowPresentation({
				...MONTHLY_OTHER_WINDOW,
				key: window.key,
				label: window.label,
				start: window.startTime,
				end: window.endTime,
				band: resolveWindowBand(window.startTime, dayBoundary),
			});
		},
		[windows, dayBoundary],
	);

	const jobTotals = useMemo(() => {
		const totals = new Map<string, number>();
		for (const window of data.windows) {
			for (const group of window.jobs) {
				totals.set(group.jobId, (totals.get(group.jobId) ?? 0) + group.employees.length);
			}
		}
		return totals;
	}, [data]);

	// The chip row (and its in-panel filter) reflects the grid's job filter, so a filtered day doesn't
	// offer job chips the grid has already excluded. The unassigned bucket is a chip only on a day that
	// actually HAS one — it is the residue of the taxonomy, not a standing member of it, so offering it
	// on every day would put a permanently-zero chip beside the department's real jobs.
	const chipJobs = useMemo(() => {
		const offered = filters.jobFilterIds.size > 0 ? visibleJobs.filter((job) => filters.jobFilterIds.has(job.id)) : visibleJobs;
		return offered.filter((job) => job.id !== MONTHLY_UNASSIGNED_JOB_ID || (jobTotals.get(job.id) ?? 0) > 0);
	}, [visibleJobs, filters.jobFilterIds, jobTotals]);

	/**
	 * The in-panel selection, narrowed to what the panel is still OFFERING.
	 *
	 * The drawer stays mounted while the grid's own job filter changes underneath it, so a chip selected
	 * a moment ago can stop being offered. The raw selection then matched nothing, every row filtered
	 * itself out, and the panel went blank with no chip left to explain why — a dead end the user could
	 * not click their way out of. Intersecting means a selection that stops being offered simply stops
	 * applying, and the panel falls back to showing everything.
	 */
	const activeJobIds = useMemo(() => {
		const offered = new Set(chipJobs.map((job) => job.id));
		return new Set([...selectedJobIds].filter((jobId) => offered.has(jobId)));
	}, [selectedJobIds, chipJobs]);

	/**
	 * What the header states — always what the rows below it actually show.
	 *
	 * It counts through the SAME two filters the rows apply (the job selection and the staff search) and
	 * counts a window only while it still holds somebody, because a window either filter empties is
	 * dropped from the list. With neither filter active this is `data.totalScheduled` by construction.
	 */
	const visibleSummary = useMemo(() => {
		const normalized = searchText.trim().toLowerCase();
		let total = 0;
		let windowCount = 0;
		for (const window of data.windows) {
			let inWindow = 0;
			for (const group of window.jobs) {
				if (activeJobIds.size > 0 && !activeJobIds.has(group.jobId)) continue;
				inWindow += group.employees.filter((employee) => matchesStaffSearch(employee, normalized)).length;
			}
			if (inWindow === 0) continue;
			total += inWindow;
			windowCount += 1;
		}
		return { total, windowCount };
	}, [data.windows, activeJobIds, searchText]);

	// The SAME action layer the grid cells use, so the two can never disagree about what is allowed.
	const actions = useMonthlyDayActions({ isInSchedule, date, showActual, isAutoScheduler, source: "sidebar" });
	const { canPost, openPanel } = actions;

	/**
	 * A window row's post action, resolved HERE so the drawer keeps one action layer and one panel.
	 *
	 * The job is only passed on when the window holds exactly one across the WHOLE day's schedule
	 * (`impliedJobId`, measured before filtering) — read off the filtered groups instead and a day
	 * narrowed to Aides makes a mixed Nurse+Aide window look single-job and prefills the wrong one. With
	 * two jobs in the window there is no implied job at all, and prefilling a guess is worse than leaving
	 * the field for the user. The times are the window's own exact times on this date, so nothing has to
	 * be re-based onto the day.
	 */
	const postWindow = useCallback(
		(window: MonthlySidebarWindow) => {
			// Guarded again here, not just at the chip: `endTime` is what makes a window postable at all.
			if (window.endTime === null) return;
			openPanel("create-shift", { startTime: window.startTime, endTime: window.endTime, jobId: window.impliedJobId ?? undefined });
		},
		[openPanel],
	);

	// This drawer's OWN date, never `StaffingDateTypeSelector`'s "today if today is in view" rule: the
	// user opened a specific day and the jump has to land on that day. Shared with the grid tile's arrow
	// (one callback, one analytics event, one route) so the two entry points cannot drift apart.
	const openDayView = useCallback(() => onOpenDayView(date), [onOpenDayView, date]);

	const toggleJobChip = (jobId: string) => {
		setSelectedJobIds((prev) => {
			const next = new Set(prev);
			if (next.has(jobId)) next.delete(jobId);
			else next.add(jobId);
			return next;
		});
	};

	return (
		<Drawer anchor="right" open onClose={onClose} slotProps={{ paper: { sx: { width: MONTHLY_SIDEBAR_WIDTH, maxWidth: "100vw" } } }}>
			<Box data-testid="monthly-day-sidebar" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
				<Box sx={{ p: 2, pb: 1 }}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<Typography sx={{ fontSize: "12px", fontWeight: 600, color: INK.SECONDARY, textTransform: "uppercase" }}>{date.toFormat("MMM d, yyyy")}</Typography>
						<IconButton size="small" onClick={onClose} aria-label={t("monthly.view.close")} data-testid="monthly-day-sidebar-close">
							<CloseIcon sx={{ fontSize: 18 }} />
						</IconButton>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
						{/* The same headline the cell carries, in the same COUNT emphasis — the drawer is where the
						    user arrives from that tile, so painting the identical number differently on the two
						    screens would make the drill-down contradict the thing that was clicked. Like the
						    tile, it states a headcount and no target. */}
						<Box sx={{ width: 72, height: 28, borderRadius: 0.75, overflow: "hidden", flexShrink: 0 }}>
							<CoverageDeltaCell
								filled={visibleSummary.total}
								target={0}
								minimum={0}
								required={false}
								displayMode="filled"
								emphasis="count"
								ariaLabel={`${showActual ? t("monthly.view.worked") : t("monthly.view.scheduled")} ${visibleSummary.total}`}
								dataTestId="monthly-sidebar-day-coverage"
							/>
						</Box>
						<Box sx={{ minWidth: 0 }}>
							<Typography sx={{ fontSize: "15px", fontWeight: 700, color: INK.PRIMARY }}>
								{showActual ? t("monthly.view.worked") : t("monthly.view.scheduled")}
							</Typography>
							<Typography sx={{ fontSize: "12px", color: INK.SECONDARY }}>
								{t("monthly.view.total.across.shifts", {
									total: visibleSummary.total,
									count: visibleSummary.windowCount,
								})}
							</Typography>
						</Box>
					</Box>

					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
						<Chip
							label={t("monthly.view.all.jobs")}
							size="small"
							onClick={() => setSelectedJobIds(new Set())}
							variant={activeJobIds.size === 0 ? "filled" : "outlined"}
							color={activeJobIds.size === 0 ? "primary" : "default"}
							sx={{ height: 24 }}
						/>
						{chipJobs.map((job) => (
							<Chip
								key={job.id}
								label={`${job.displayName} ${jobTotals.get(job.id) ?? 0}`}
								size="small"
								onClick={() => toggleJobChip(job.id)}
								variant={activeJobIds.has(job.id) ? "filled" : "outlined"}
								color={activeJobIds.has(job.id) ? "primary" : "default"}
								sx={{ height: 24 }}
							/>
						))}
					</Box>

					{/* Day actions + the jump to the full day view. Visible at rest (this is chrome, not a
					    hover reveal) and tabbable, unlike the grid cell's copy of the same row. Past days get
					    the jump only — there is no plan left to change. */}
					<ChatActionBar alwaysVisible dataTestId="monthly-day-sidebar-actions" sx={{ mt: 1 }}>
						<ChatActionButton
							label={t("monthly.view.open.day.view")}
							icon={<EventNoteOutlinedIcon />}
							onClick={openDayView}
							dataTestId="monthly-day-sidebar-open-day-view"
						/>
						{!showActual && <MonthlyDayActions actions={actions} focusable dataTestIdPrefix="monthly-day-sidebar" />}
					</ChatActionBar>

					<TextField
						fullWidth
						size="small"
						value={searchText}
						onChange={(event) => setSearchText(event.target.value)}
						placeholder={t("monthly.view.search.name.role.location")}
						sx={{ mt: 1.5 }}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<Search sx={{ fontSize: 18, color: INK.DISABLED }} />
									</InputAdornment>
								),
							},
						}}
					/>
				</Box>
				<Divider />

				<Box sx={{ flex: 1, overflowY: "auto", backgroundColor: SURFACE }}>
					{data.windows.length === 0 && <Typography sx={{ p: 2, fontSize: "13px", color: INK.SECONDARY }}>{t("monthly.view.no.shifts.this.day")}</Typography>}
					{data.windows.map((window) => (
						<Box key={window.key} data-testid={`monthly-sidebar-window-${window.key}`}>
							<WindowRow
								window={window}
								presentation={presentationFor(window)}
								selectedJobIds={activeJobIds}
								searchText={searchText}
								departmentId={departmentId}
								onPostWindow={postWindow}
								canPost={canPost}
							/>
						</Box>
					))}
				</Box>

				{/* Mount point for the staffing-changes panel — renders nothing until an action is invoked. Kept
				    outside the scrolling roster so it can never be clipped or scrolled away mid-flow. */}
				{!showActual && actions.overlays}
			</Box>
		</Drawer>
	);
};
